import { CouponsRepository } from '@lib/api/repository/coupons';
import {
  Coupon,
  CouponInput,
  CouponQuoteInput,
  CouponScope,
  SaveCouponInput,
} from '@lib/types/coupons';

import { Requester } from '../clients';

interface CouponDTO {
  id: string;
  code: string;
  type: 'STORE' | 'PLATFORM';
  storeId?: string;
  discountType: 'PERCENTAGE' | 'FIXED';
  value: number;
  maximumDiscountCents?: number;
  minimumCents: number;
  startsAt: string;
  expiresAt: string;
  totalLimit?: number;
  customerLimit?: number;
  storeIds: string[];
  categories: string[];
  firstPurchaseOnly: boolean;
  active: boolean;
  archivedAt?: string;
  whitelistCount: number;
  reservedUses: number;
  confirmedUses: number;
  grantedCents: number;
  createdAt: string;
  updatedAt: string;
}
interface CouponPriceDTO {
  subtotalCents: number;
  shippingCents: number;
  totalCents: number;
  storeDiscountCents: number;
  platformDiscountCents: number;
  payableItemsCents: number;
  commissionBaseCents: number;
  allocations: Array<{
    couponId: string;
    code: string;
    type: 'STORE' | 'PLATFORM';
    storeId: string;
    itemId: string;
    amountCents: number;
  }>;
}
interface CouponUsageDTO {
  id: string;
  code: string;
  orderId: string;
  buyerId: string;
  amountCents: number;
  state: 'RESERVED' | 'CONFIRMED' | 'RELEASED' | 'REVERSED';
  createdAt: string;
  confirmedAt?: string;
  reversedAt?: string;
}
const pathFor = (scope: CouponScope) =>
  scope === 'platform' ? '/marketplace/admin/coupons' : '/marketplace/coupons';
const toCoupon = (data: CouponDTO): Coupon => ({
  id: data.id,
  code: data.code,
  type: data.type,
  storeId: data.storeId,
  discountType: data.discountType,
  value: data.value,
  maximumDiscountCents: data.maximumDiscountCents,
  minimumCents: data.minimumCents,
  startsAt: data.startsAt,
  expiresAt: data.expiresAt,
  totalLimit: data.totalLimit,
  customerLimit: data.customerLimit,
  storeIds: data.storeIds,
  categories: data.categories,
  firstPurchaseOnly: data.firstPurchaseOnly,
  active: data.active,
  archivedAt: data.archivedAt,
  whitelistCount: data.whitelistCount,
  reservedUses: data.reservedUses,
  confirmedUses: data.confirmedUses,
  grantedCents: data.grantedCents,
  createdAt: data.createdAt,
  updatedAt: data.updatedAt,
});
const toInput = (input: CouponInput) => ({
  code: input.code,
  discountType: input.discountType,
  value: input.value,
  maximumDiscountCents: input.maximumDiscountCents,
  minimumCents: input.minimumCents,
  startsAt: input.startsAt,
  expiresAt: input.expiresAt,
  totalLimit: input.totalLimit,
  customerLimit: input.customerLimit,
  storeIds: input.storeIds,
  categories: input.categories,
  firstPurchaseOnly: input.firstPurchaseOnly,
  active: input.active,
  allowedCpfs: input.allowedCpfs,
});

export default class CouponsAPI implements CouponsRepository {
  async list(scope: CouponScope, page: number, signal?: AbortSignal) {
    const { data } = await Requester.get<{ items: CouponDTO[]; total: number }>(
      pathFor(scope),
      { signal, params: { page } },
    );
    return { items: data.items.map(toCoupon), total: data.total };
  }

  async save({ scope, id, input }: SaveCouponInput) {
    const { data } = id
      ? await Requester.patch<CouponDTO>(
          `${pathFor(scope)}/${encodeURIComponent(id)}`,
          { body: toInput(input) },
        )
      : await Requester.post<CouponDTO>(pathFor(scope), {
          body: toInput(input),
        });
    return toCoupon(data);
  }

  async archive(scope: CouponScope, id: string) {
    const { data } = await Requester.post<CouponDTO>(
      `${pathFor(scope)}/${encodeURIComponent(id)}/archive`,
    );
    return toCoupon(data);
  }

  async usages(
    scope: CouponScope,
    id: string,
    page: number,
    signal?: AbortSignal,
  ) {
    const { data } = await Requester.get<{
      items: CouponUsageDTO[];
      total: number;
    }>(`${pathFor(scope)}/${encodeURIComponent(id)}/usages`, {
      signal,
      params: { page },
    });
    return {
      items: data.items.map((item) => ({
        id: item.id,
        code: item.code,
        orderId: item.orderId,
        buyerId: item.buyerId,
        amountCents: item.amountCents,
        state: item.state,
        createdAt: item.createdAt,
        confirmedAt: item.confirmedAt,
        reversedAt: item.reversedAt,
      })),
      total: data.total,
    };
  }

  async quote(input: CouponQuoteInput, signal?: AbortSignal) {
    const { data } = await Requester.post<CouponPriceDTO>(
      '/marketplace/coupons/quote',
      {
        signal,
        body: {
          codes: input.codes,
          storeIds: input.storeIds,
          shippingQuoteIds: input.shippingQuoteIds,
        },
      },
    );
    return {
      subtotalCents: data.subtotalCents,
      shippingCents: data.shippingCents,
      totalCents: data.totalCents,
      storeDiscountCents: data.storeDiscountCents,
      platformDiscountCents: data.platformDiscountCents,
      payableItemsCents: data.payableItemsCents,
      commissionBaseCents: data.commissionBaseCents,
      allocations: data.allocations.map((item) => ({
        couponId: item.couponId,
        code: item.code,
        type: item.type,
        storeId: item.storeId,
        itemId: item.itemId,
        amountCents: item.amountCents,
      })),
    };
  }
}
